import { supabase } from "../lib/supabase";

/**
 * Container document types enum
 */
export enum ContainerDocumentType {
  AWB_BL = "awb_bl",
  VRIJSTELLING = "vrijstelling",
  T1_DOCUMENT = "t1_document",
}

/**
 * Sanitize filename to prevent special characters and spaces
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`;
  
  return fileName
    .replace(/\s+/g, "_")
    .replace(/[^\w.-]/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

/**
 * Generate a file path for container documents
 * Structure: containers/{container_id}/{document_type}/{timestamp}_{filename}
 */
export const generateContainerDocumentFilePath = (
  containerId: number | string,
  documentType: ContainerDocumentType,
  fileName: string
): string => {
  const sanitizedFileName = sanitizeFileName(fileName);
  return `containers/${containerId}/${documentType}/${Date.now()}_${sanitizedFileName}`;
};

/** AWB/BL stored on master BL/AWB (one file per master). Admin/agent only. */
export const generateMasterContainerDocumentFilePath = (
  masterContainerId: number | string,
  fileName: string
): string => {
  const sanitizedFileName = sanitizeFileName(fileName);
  return `masters/${masterContainerId}/awb_bl/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload a master AWB/BL document file (same bucket as container documents).
 */
export const uploadMasterContainerDocument = async (
  masterContainerId: number | string,
  file: File
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      return { error: "No file provided" };
    }

    const filePath = generateMasterContainerDocumentFilePath(
      masterContainerId,
      file.name
    );

    const { data, error } = await supabase.storage
      .from("container_documents")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      return { error };
    }

    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadMasterContainerDocument:", error);
    return { error };
  }
};

/**
 * Upload a container document file
 */
export const uploadContainerDocument = async (
  containerId: number | string,
  documentType: ContainerDocumentType,
  file: File
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }

    console.log("uploadContainerDocument starting with:", {
      containerId,
      documentType,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    });

    const filePath = generateContainerDocumentFilePath(
      containerId,
      documentType,
      file.name
    );

    console.log("Generated file path:", filePath);

    // Upload to Supabase storage
    console.log("Uploading to bucket: container_documents");
    const { data, error } = await supabase.storage
      .from("container_documents")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("Error uploading container document:", error);

      // Provide more detailed error information
      if (
        error.message?.includes("Bucket not found") ||
        error.statusCode === "404"
      ) {
        return {
          error: {
            ...error,
            message:
              "Bucket not found: 'container_documents'. Please create this bucket in Supabase Storage.",
            userMessage:
              "The storage bucket needs to be created. See instructions in the error message.",
          },
        };
      }

      return { error };
    }

    console.log("Container document uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadContainerDocument:", error);
    return { error };
  }
};

/**
 * Get public URL for a container document file
 */
export const getContainerDocumentPublicUrl = (
  path: string
): string | null => {
  try {
    if (!path || path.trim() === "") {
      return null;
    }
    
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data } = supabase.storage
      .from("container_documents")
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Create a signed URL for container document file
 */
export const createContainerDocumentSignedUrl = async (
  path: string,
  expiresIn: number = 3600 // Default 1 hour for documents
): Promise<string | null> => {
  void expiresIn;
  try {
    if (!path || path.trim() === "") {
      return null;
    }
    
    const cleanPath = path.trim().replace(/^\//, "");
    const { data, error } = await supabase.storage
      .from("container_documents")
      .download(cleanPath);

    if (error || !data) {
      console.error("Error loading container document for preview:", error);
      return null;
    }

    return URL.createObjectURL(data);
  } catch (error) {
    console.error("Error in createContainerDocumentSignedUrl:", error);
    return null;
  }
};

/**
 * Get file type from extension
 */
export const getFileType = (filename: string): {
  type: "pdf" | "image" | "excel" | "word" | "other";
  extension: string;
  mimeType: string;
} => {
  const extension = filename.split(".").pop()?.toLowerCase() || "";
  
  const mimeTypes: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    txt: "text/plain",
    csv: "text/csv",
  };
  
  if (extension === "pdf") {
    return { type: "pdf", extension, mimeType: mimeTypes.pdf };
  }
  
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(extension)) {
    return {
      type: "image",
      extension,
      mimeType: mimeTypes[extension] || "image/jpeg",
    };
  }
  
  if (["xls", "xlsx"].includes(extension)) {
    return {
      type: "excel",
      extension,
      mimeType: mimeTypes[extension] || "application/vnd.ms-excel",
    };
  }
  
  if (["doc", "docx"].includes(extension)) {
    return {
      type: "word",
      extension,
      mimeType: mimeTypes[extension] || "application/msword",
    };
  }
  
  return {
    type: "other",
    extension,
    mimeType: mimeTypes[extension] || "application/octet-stream",
  };
};

/**
 * Download a container document file with proper handling for different file types
 */
export const downloadContainerDocument = async (
  path: string,
  filename: string
): Promise<void> => {
  try {
    if (!path || path.trim() === "") {
      throw new Error("Invalid file path provided");
    }
    
    const cleanPath = path.trim().replace(/^\//, "");
    const fileInfo = getFileType(filename);

    console.log("Downloading container document:", cleanPath, fileInfo);

    const { data: blob, error } = await supabase.storage
      .from("container_documents")
      .download(cleanPath);

    if (error || !blob) {
      console.error("Error downloading container document:", error);
      throw new Error("Could not download file");
    }

    const objectUrl = URL.createObjectURL(blob);

    if (fileInfo.type === "pdf") {
      console.log("Opening PDF in new tab");
      window.open(objectUrl, "_blank");
      return;
    }

    const downloadBlob = new Blob([blob], { type: fileInfo.mimeType });
    const downloadUrl = window.URL.createObjectURL(downloadBlob);
    URL.revokeObjectURL(objectUrl);

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    console.log("Download completed for:", filename);
  } catch (error) {
    console.error("Error downloading container document:", error);

    try {
      const cleanPath = path.trim().replace(/^\//, "");
      const { data: blob, error: err2 } = await supabase.storage
        .from("container_documents")
        .download(cleanPath);
      if (!err2 && blob) {
        const u = URL.createObjectURL(blob);
        console.log("Fallback: opening blob URL in new tab");
        window.open(u, "_blank");
      }
    } catch (fallbackError) {
      throw error;
    }
  }
};

/**
 * Get preview URL for a container document (for PDFs and images)
 */
export const getContainerDocumentPreviewUrl = async (
  path: string
): Promise<string | null> => {
  try {
    const cleanPath = path.trim().replace(/^\//, "");
    // Create a signed URL with longer expiry for previews (1 hour)
    return await createContainerDocumentSignedUrl(cleanPath, 3600);
  } catch (error) {
    console.error("Error getting preview URL:", error);
    return null;
  }
};

/**
 * Delete a container document file from storage
 */
export const deleteContainerDocumentFile = async (
  path: string
): Promise<{ error: unknown } | null> => {
  try {
    if (!path || path.trim() === "") {
      return { error: "Invalid file path provided" };
    }
    
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Deleting container document from storage:", cleanPath);
    
    const { error } = await supabase.storage
      .from("container_documents")
      .remove([cleanPath]);
    
    if (error) {
      console.error("Error deleting document file from storage:", error);
      return { error };
    }
    
    console.log("Document file deleted successfully");
    return { error: null };
  } catch (error) {
    console.error("Error in deleteContainerDocumentFile:", error);
    return { error };
  }
};

